import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DocumentDistributionPanel } from "@/components/workflows/DocumentDistributionPanel";
import { GovernanceFilingPanel } from "@/components/workflows/GovernanceFilingPanel";

interface AssetTransfersPhase3Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

export function AssetTransfersPhase3({
  taskId,
  unaId,
  task,
  phaseData,
  onPhaseComplete,
}: AssetTransfersPhase3Props) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [filingComplete, setFilingComplete] = useState(false);
  const [completingPhase, setCompletingPhase] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (task?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [task]);

  useEffect(() => {
    loadDocuments();
    checkCompletionStatus();
  }, [taskId]);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from("generated_documents")
        .select("*")
        .eq("task_id", taskId)
        .in("status", ["final", "finalized"])
        .order("generated_at");

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const checkCompletionStatus = async () => {
    await Promise.all([
      checkDistributionComplete(),
      checkFilingComplete(),
    ]);
  };

  const checkDistributionComplete = async () => {
    try {
      const { data, error } = await supabase
        .from("document_distribution_log")
        .select("id")
        .eq("task_id", taskId)
        .limit(1);

      if (error) throw error;
      setDistributionComplete((data?.length || 0) > 0);
    } catch (error) {
      console.error("Error checking distribution status:", error);
    }
  };

  const checkFilingComplete = async () => {
    try {
      // Get all documents for this task
      const { data: allDocs } = await supabase
        .from("generated_documents")
        .select("id, document_name")
        .eq("task_id", taskId)
        .in("status", ["final", "finalized"]);

      // Filter to non-resolution documents (these go to Document Repository)
      const nonResolutionDocs = (allDocs || []).filter(doc => 
        !doc.document_name?.toLowerCase().includes('resolution')
      );

      // If there are no non-resolution docs to file, consider filing complete
      if (nonResolutionDocs.length === 0) {
        setFilingComplete(true);
        return;
      }

      // Count filed non-resolution documents
      const { count: filedDocs } = await supabase
        .from("governance_documents")
        .select("id", { count: "exact", head: true })
        .eq("generated_from_task_id", taskId);

      setFilingComplete((filedDocs || 0) >= 1);
    } catch (error) {
      console.error("Error checking filing status:", error);
    }
  };

  const handleDistributionComplete = async () => {
    await checkDistributionComplete();
  };

  const handleFilingComplete = async () => {
    await checkFilingComplete();
  };

  const handleMarkComplete = async () => {
    setCompletingPhase(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "complete-asset-contribution",
        {
          body: {
            taskId,
            unaId,
            workflowData: task.workflow_data,
          },
        }
      );

      if (error) throw error;

      setIsCompleted(true);
      toast.success(
        `Asset Contribution Complete! Created contribution record and ${data.tasks_created} follow-up tasks.`
      );
      onPhaseComplete?.();
    } catch (error) {
      console.error("Error completing Phase 3:", error);
      toast.error("Failed to complete Phase 3");
    } finally {
      setCompletingPhase(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Distribution is optional, only filing is required
  const allComplete = filingComplete;

  // Filter documents: Resolution documents go to Resolutions & Actions section automatically
  // Only non-resolution documents go to Document Repository via GovernanceFilingPanel
  const nonResolutionDocs = documents.filter(doc => 
    !doc.document_name?.toLowerCase().includes('resolution')
  );

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <CardTitle>Phase 3: Record & Update</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            File documents to governance records and establish ongoing asset stewardship.
          </p>

          <Alert>
            <AlertDescription>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {distributionComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                  )}
                  <span className="text-sm">
                    Distribute contribution documents to relevant parties
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {filingComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                  )}
                  <span className="text-sm">
                    File non-resolution documents to Document Repository
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {allComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                  )}
                  <span className="text-sm">
                    Create asset tracking records & file resolutions
                  </span>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* Info about resolution routing */}
          <Alert className="border-blue-200 bg-blue-50">
            <AlertDescription className="text-blue-800 text-sm">
              <strong>Note:</strong> Resolution documents (Authorization Resolution, Resolution of Acceptance) 
              will be automatically filed to the Resolutions & Actions section when you complete this phase. 
              Other documents will be filed to the Document Repository below.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Document Distribution Panel - All documents can be distributed */}
      <DocumentDistributionPanel
        taskId={taskId}
        unaId={unaId}
        documents={documents}
        onDistributionComplete={handleDistributionComplete}
      />

      {/* Governance Filing Panel - Only non-resolution documents */}
      {nonResolutionDocs.length > 0 && (
        <GovernanceFilingPanel
          taskId={taskId}
          unaId={unaId}
          documents={nonResolutionDocs}
          workflowKey="asset_transfers_contributions"
          onFilingComplete={handleFilingComplete}
        />
      )}

      {/* Completion Status & Action */}
      {isCompleted ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Asset Contribution Workflow Completed</p>
                <p className="text-sm text-green-700">
                  All documents have been distributed and filed. Resolutions have been added to 
                  Resolutions & Actions. Follow-up stewardship tasks have been created.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Alert className={!allComplete ? 'border-muted' : 'border-green-200 bg-green-50'}>
            <CheckCircle2 className={`h-4 w-4 ${allComplete ? 'text-green-600' : 'text-muted-foreground'}`} />
            <AlertDescription className={allComplete ? 'text-green-800' : ''}>
              <div className="space-y-2">
                <p className="font-medium">
                  {allComplete ? 'All Requirements Complete!' : 'Complete Phase 3'}
                </p>
                <p className="text-sm">
                  {allComplete 
                    ? 'You can now mark Phase 3 as complete to create asset tracking records and generate follow-up stewardship tasks.'
                    : 'File at least one document to governance to complete. Distribution is optional.'
                  }
                </p>
              </div>
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent className="pt-6">
              <Button
                onClick={handleMarkComplete}
                disabled={!allComplete || completingPhase}
                className="w-full"
                size="lg"
              >
                {completingPhase ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Records...
                  </>
                ) : (
                  "Complete Asset Contribution Workflow"
                )}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
